import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Row, Col, Badge, Modal, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSave, 
  faCog,
  faPercentage,
  faCalculator,
  faSignOutAlt,
  faInfoCircle,
  faMoneyBillWave,
  faUpload,
  faImage,
  faTrash,
  faKey,
  faEye,
  faEyeSlash,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contractService';
import { userService } from '../../services/userService';
import { upgradeRequestService } from '../../services/upgradeRequestService';
import { formatCurrency, getCurrencySymbol } from '../../utils/currencyFormatter';

const AdminFeesSettings = () => {
  const navigate = useNavigate();
  const { userCompany, logout } = useAuth();
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    brokeragePercentage: '5',
    currency: 'USD',
    logoUrl: ''
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Password change modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Upgrade request states
  const [logoEnabled, setLogoEnabled] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [requestingUpgrade, setRequestingUpgrade] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [userCompany]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [settingsData, hasPending, hasApproved] = await Promise.all([
        contractService.getBrokerageSettings(userCompany),
        upgradeRequestService.hasPendingRequest(userCompany, 'logo_upload'),
        upgradeRequestService.hasApprovedRequest(userCompany, 'logo_upload')
      ]);
      
      setSettings(settingsData);
      setLogoEnabled(settingsData.logoEnabled || hasApproved);
      setHasPendingRequest(hasPending);
      
      setFormData({
        brokeragePercentage: settingsData.brokeragePercentage?.toString() || '5',
        currency: settingsData.currency || 'USD',
        logoUrl: settingsData.logoUrl || ''
      });
      
      // Set logo preview if exists
      if (settingsData.logoUrl) {
        setLogoPreview(settingsData.logoUrl);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
              setError('Failed to load admin fees settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }
      
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      setError('Please select a logo file first');
      return;
    }

    try {
      setUploadingLogo(true);
      setError('');
      
      const logoUrl = await contractService.uploadCompanyLogo(userCompany, logoFile);
      setLogoPreview(logoUrl);
      setLogoFile(null);
      setSuccess('Logo uploaded successfully!');
      
      // Refresh settings to get updated logo URL
      await fetchSettings();
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Failed to upload logo: ' + (error.message || error));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await contractService.removeCompanyLogo(userCompany);
      setLogoPreview(null);
      setLogoFile(null);
      setSuccess('Logo removed successfully!');
      
      // Refresh settings
      await fetchSettings();
    } catch (error) {
      console.error('Error removing logo:', error);
      setError('Failed to remove logo: ' + (error.message || error));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const percentage = parseFloat(formData.brokeragePercentage);
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      setError('Please enter a valid percentage between 0 and 100');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await contractService.updateBrokerageSettings(userCompany, percentage, formData.currency, formData.logoUrl);
      
      setSuccess('Admin fees settings updated successfully!');
      fetchSettings();
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Failed to update admin fees settings: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const calculateExample = (amount) => {
    const percentage = parseFloat(formData.brokeragePercentage) || 0;
    const currency = formData.currency || 'USD';
    const adminFee = (amount * percentage) / 100;
    const totalAmount = amount + adminFee;
    
    return {
      rentalAmount: amount,
      adminFee: parseFloat(adminFee.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      currency
    };
  };

  const handleUpgradeRequest = async () => {
    try {
      setRequestingUpgrade(true);
      setError('');
      
      await upgradeRequestService.createUpgradeRequest(
        userCompany, 
        'logo_upload', 
        'Request to enable custom logo upload feature for company branding'
      );
      
      setHasPendingRequest(true);
      setSuccess('Upgrade request submitted successfully! We will review your request and get back to you soon.');
    } catch (error) {
      console.error('Error submitting upgrade request:', error);
      setError('Failed to submit upgrade request: ' + (error.message || error));
    } finally {
      setRequestingUpgrade(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setChangingPassword(true);
      setError('');
      
      await userService.updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      
      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Show success message
      alert('Password updated successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Failed to change password: ' + (error.message || error));
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/dashboard')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Dashboard
          </Button>
          <div>
            <h2 className="mb-1">Admin Fees Settings</h2>
            <p className="text-muted mb-0">Configure admin fees percentage for property contracts</p>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          {/* Admin Fees Settings Form */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faCog} className="text-primary me-2" />
                Admin Fees Configuration
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faPercentage} className="me-1" />
                        Admin Fees Percentage <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="brokeragePercentage"
                        value={formData.brokeragePercentage}
                        onChange={handleInputChange}
                        placeholder="Enter percentage"
                        min="0"
                        max="100"
                        step="0.01"
                        required
                      />
                      <Form.Text className="text-muted">
                        Enter the percentage of rental amount to be charged as admin fees
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                        Currency <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                        <option value="AED">AED (د.إ)</option>
                        <option value="SAR">SAR (ر.س)</option>
                        <option value="PKR">PKR (₨)</option>
                        <option value="INR">INR (₹)</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select the currency for all financial calculations
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Company Logo URL */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faImage} className="text-primary me-2" />
                Company Logo URL
              </h5>
            </Card.Header>
            <Card.Body>
              {!logoEnabled && (
                <div className="mb-4">
                  <Alert variant="info">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-1">
                          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                          Premium Feature
                        </h6>
                        <p className="mb-2">
                          To add your own logo to the website and contract PDFs, please upgrade your account.
                        </p>
                        {hasPendingRequest && (
                          <Badge bg="warning" className="me-2">
                            <FontAwesomeIcon icon={faClock} className="me-1" />
                            Request Pending
                          </Badge>
                        )}
                      </div>
                      {!hasPendingRequest && (
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={handleUpgradeRequest}
                          disabled={requestingUpgrade}
                        >
                          <FontAwesomeIcon icon={faUpload} className="me-2" />
                          {requestingUpgrade ? 'Requesting...' : 'Request Upgrade'}
                        </Button>
                      )}
                    </div>
                  </Alert>
                </div>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Logo Image URL</Form.Label>
                <Form.Control
                  type="url"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleInputChange}
                  placeholder="https://yourdomain.com/logo.png"
                  disabled={!logoEnabled}
                />
                <Form.Text className="text-muted">
                  {logoEnabled 
                    ? "Must be a direct link to a PNG, JPG, or GIF image."
                    : "Logo upload is a premium feature. Request an upgrade to enable this feature."
                  }
                </Form.Text>
              </Form.Group>
              
              <div className="text-center mt-3">
                <h6 className="mb-2">Logo Preview</h6>
                {formData.logoUrl && logoEnabled ? (
                  <div className="border rounded p-3">
                    <img
                      src={formData.logoUrl}
                      alt="Company Logo Preview"
                      className="img-fluid"
                      style={{ maxHeight: '150px', maxWidth: '100%' }}
                    />
                  </div>
                ) : !logoEnabled ? (
                  <div className="border rounded p-3">
                                         <img
                       src="/urproperty-logo.png"
                       alt="Default URProperty Logo"
                       className="img-fluid"
                       style={{ maxHeight: '200px', maxWidth: '100%' }}
                     />
                    <p className="text-muted mt-2 mb-0">Default URProperty Logo (shown when feature not enabled)</p>
                  </div>
                ) : (
                  <div className="border rounded p-5 text-muted">
                    <FontAwesomeIcon icon={faImage} size="3x" className="mb-3" />
                    <p className="mb-0">No logo URL provided</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Change Password Section */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faKey} className="text-warning me-2" />
                Change Password
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">
                Update your account password to keep your account secure.
              </p>
              <Button 
                variant="outline-warning" 
                onClick={() => setShowPasswordModal(true)}
              >
                <FontAwesomeIcon icon={faKey} className="me-2" />
                Change Password
              </Button>
            </Card.Body>
          </Card>

          {/* Save/Cancel Buttons */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex gap-2">
                <Button 
                  onClick={handleSubmit}
                  variant="primary" 
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
                
                <Button 
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard')}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Information Card */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faInfoCircle} className="text-info me-2" />
                How Admin Fees Works
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <h6>Admin Fees Calculation</h6>
                <p className="text-muted mb-2">
                  The admin fees is calculated as a percentage of the total rental amount and is added to the contract total.
                </p>
                <ul className="text-muted">
                  <li>Admin Fees = Total Rental Amount × Admin Fees Percentage</li>
                  <li>Total Contract Amount = Total Rental Amount + Admin Fees</li>
                  <li>Cheque Amount = Total Rental Amount ÷ Number of Cheques</li>
                </ul>
              </div>
              
              <div className="mb-3">
                <h6>Example Calculation</h6>
                <p className="text-muted mb-2">
                  For a rental amount of $12,000 with {formData.brokeragePercentage}% admin fees:
                </p>
                <ul className="text-muted">
                  <li>Rental Amount: {formatCurrency(12000, formData.currency)}</li>
                  <li>Admin Fees: {formatCurrency(calculateExample(12000).adminFee, formData.currency)}</li>
                  <li>Total Amount: {formatCurrency(calculateExample(12000).totalAmount, formData.currency)}</li>
                </ul>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Current Settings Summary */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Current Settings</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Admin Fees Percentage:</span>
                  <Badge bg="primary" className="fs-6">
                    {settings?.brokeragePercentage || 5}%
                  </Badge>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Currency:</span>
                  <Badge bg="info" className="fs-6">
                    {settings?.currency || 'USD'}
                  </Badge>
                </div>
              </div>
              
              {settings?.createdAt && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Last Updated:</span>
                    <small className="text-muted">
                      {settings.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </small>
                  </div>
                </div>
              )}
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Status:</span>
                  <Badge bg="success">Active</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Calculation Examples */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faCalculator} className="text-primary me-2" />
                Calculation Examples
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <h6>Rental: {formatCurrency(6000, formData.currency)}</h6>
                <div className="small">
                  <div className="d-flex justify-content-between">
                    <span>Admin Fees:</span>
                    <span>{formatCurrency(calculateExample(6000).adminFee, formData.currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Total:</span>
                    <strong>{formatCurrency(calculateExample(6000).totalAmount, formData.currency)}</strong>
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <h6>Rental: {formatCurrency(12000, formData.currency)}</h6>
                <div className="small">
                  <div className="d-flex justify-content-between">
                    <span>Admin Fees:</span>
                    <span>{formatCurrency(calculateExample(12000).adminFee, formData.currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Total:</span>
                    <strong>{formatCurrency(calculateExample(12000).totalAmount, formData.currency)}</strong>
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <h6>Rental: {formatCurrency(24000, formData.currency)}</h6>
                <div className="small">
                  <div className="d-flex justify-content-between">
                    <span>Admin Fees:</span>
                    <span>{formatCurrency(calculateExample(24000).adminFee, formData.currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Total:</span>
                    <strong>{formatCurrency(calculateExample(24000).totalAmount, formData.currency)}</strong>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Password Change Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            Change Password
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePasswordChange}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordInputChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordInputChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordInputChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={changingPassword}>
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminFeesSettings; 